import { IntelligenceModels } from '@models/intelligence.model';
import { IssuesModel } from '@models/issues.model';
import { PulsationModels } from '@models/pulsation.model';

import HealthChip = IntelligenceModels.DeviceHealth.HealthChip;
import HealthChipTone = IntelligenceModels.DeviceHealth.HealthChipTone;
import HealthMetric = IntelligenceModels.DeviceHealth.HealthMetric;
import ComponentState = IntelligenceModels.DeviceHealth.ComponentState;

/**
 * Pure view mappers of the device-health panel: a device breakdown → the chips
 * and metric fields the template renders. No Angular, no IO — the component
 * only picks between the camera and the Core variant.
 */

const EM_DASH = '—';
/** Not-applicable components keep the device-list dialog's own grey cross. */
const NO_DATA_ICON_CLASS = 'ui-icon-cross';
/** Same glyph the device-list dialog gives its trained row. */
const TRAINED_ICON_CLASS = 'ui-icon-trained';

/**
 * A component status that is NOT a fault — the mirror of the server's own rule
 * (`DeviceHealthModels.isHealthyComponent`). `notRecording` counts as healthy
 * because the overall-status rule accepts it, so painting it red here would
 * contradict the Online the same response reports.
 */
function isHealthyStatus(status: PulsationModels.ComponentStatus): boolean {
  return (
    status === PulsationModels.ComponentStatus.Online ||
    status === PulsationModels.ComponentStatus.Streaming ||
    status === PulsationModels.ComponentStatus.Connected ||
    status === PulsationModels.ComponentStatus.NotRecording
  );
}

/**
 * One component chip.
 *
 * A healthy component carries NO note: the tone already says "fine", and a row
 * of chips each repeating "Online" is the redundancy the chips replace — the
 * wording moves to the tooltip, which every chip has. Faults and
 * accepted-but-notable states keep their wording on the chip as well.
 *
 * A component that does not apply carries neither: an em dash next to a greyed
 * label says nothing the grey did not, so the reason goes in the tooltip.
 */
function componentChip(labelKey: string, state: ComponentState): HealthChip {
  if (!state.applicable || state.status === undefined) {
    return {
      labelKey,
      tone: HealthChipTone.Muted,
      iconClass: NO_DATA_ICON_CLASS,
      tooltipKey: 'DEVICE_HEALTH.NOT_CONFIGURED',
    };
  }

  const media = mediaNotes(state.media);
  const note = IntelligenceModels.DeviceHealth.ComponentStatusLabel[state.status];
  if (state.status === PulsationModels.ComponentStatus.NotRecording) {
    return { labelKey, tone: HealthChipTone.Muted, note, tooltip: note, ...media };
  }
  if (isHealthyStatus(state.status)) {
    return { labelKey, tone: HealthChipTone.Ok, tooltip: note, ...media };
  }
  if (state.status === PulsationModels.ComponentStatus.Offline) {
    return { labelKey, tone: HealthChipTone.Error, note, tooltip: note, ...media };
  }
  return { labelKey, tone: HealthChipTone.Warning, note, tooltip: note, ...media };
}

/**
 * The resolution/codec chips `camera-health-row` renders beside a stream
 * component, in its order: width×height (only when BOTH are present), video
 * codec, audio codec. Returns a spreadable fragment so a media-free component
 * carries no `media` key at all.
 */
function mediaNotes(
  media: IntelligenceModels.DeviceHealth.StreamMedia | undefined,
): Pick<HealthChip, 'media'> {
  if (!media) return {};

  const notes: string[] = [];
  if (media.width && media.height) notes.push(`${media.width}x${media.height}`);
  if (media.videoCodec) notes.push(media.videoCodec);
  if (media.audioCodec) notes.push(media.audioCodec);
  return notes.length ? { media: notes } : {};
}

/**
 * Training progress, word for word the device-list dialog's own three states
 * (`ui-camera-status.component.html:71-108`): its `PROPER_FITTING_TRAINED`,
 * its `PROPER_FITTING_TEXT` with the same `daysToGoal`, and nothing at all when
 * there is no estimate. The chip shows the days; the sentence is the tooltip.
 *
 * Tones follow that dialog too (`ui-camera-status.component.scss:19-29`):
 * trained is `--color-icon-success`, everything else `--color-icon-warning`.
 */
function trainedChip(trained: IntelligenceModels.DeviceHealth.Trained): HealthChip {
  const labelKey = 'LOCATION_LIST.TRAINED';

  if (trained.state === IntelligenceModels.DeviceHealth.TrainedState.Trained) {
    return {
      labelKey,
      tone: HealthChipTone.Ok,
      iconClass: TRAINED_ICON_CLASS,
      tooltipKey: 'LOCATION_LIST.PROPER_FITTING_TRAINED',
    };
  }
  if (trained.state === IntelligenceModels.DeviceHealth.TrainedState.NotTrained && trained.daysToGoal) {
    return {
      labelKey,
      tone: HealthChipTone.Warning,
      iconClass: TRAINED_ICON_CLASS,
      note: `${trained.daysToGoal} d`,
      tooltipKey: 'LOCATION_LIST.PROPER_FITTING_TEXT',
      tooltipParams: { daysToGoal: trained.daysToGoal },
    };
  }

  if (trained.state === IntelligenceModels.DeviceHealth.TrainedState.Unknown) {
    /**
     * No proper-fitting row at all (or the stats bus was unreachable). The
     * dialog shows a grey trained icon and an em dash with no wording of its
     * own (`ui-camera-status.component.html:80-93`), so no tooltip either.
     */
    return {
      labelKey,
      tone: HealthChipTone.Muted,
      iconClass: TRAINED_ICON_CLASS,
      note: EM_DASH,
    };
  }

  /**
   * No retraining estimate. The dialog prints an empty description here, but a
   * chip with no note and no tooltip says nothing at all, so it carries the
   * first sentence of that same dialog string — never a wording of our own.
   */
  return {
    labelKey,
    tone: HealthChipTone.Warning,
    iconClass: TRAINED_ICON_CLASS,
    tooltipKey: 'LOCATION_LIST.PROPER_FITTING_NOT_TRAINED',
  };
}

/** Camera component strip, in the order the device-list dialog lists them. */
export function toCameraChips(details: IntelligenceModels.DeviceHealth.CameraDetails): HealthChip[] {
  return [
    componentChip('LOCATION_LIST.STREAM', details.mainStream),
    componentChip('LOCATION_LIST.SUB_STREAM', details.subStream),
    componentChip('LOCATION_LIST.ANALYTIC', details.analytics),
    componentChip('LOCATION_LIST.STORAGE', details.storage),
    componentChip('LOCATION_LIST.SMART_STORAGE', details.smartStorage),
    componentChip('LOCATION_LIST.PLAYBACK', details.playback),
    trainedChip(details.trained),
  ];
}

/**
 * Core component strip. Both statuses are always rendered — the Core dialog
 * shows them unconditionally, and a missing one is information too.
 */
export function toCoreChips(details: IntelligenceModels.DeviceHealth.CoreDetails): HealthChip[] {
  return [
    componentChip('LOCATION_LIST.EDGE_STATUS', { status: details.edgeStatus, applicable: true }),
    componentChip('LOCATION_LIST.PLAYBACK', { status: details.playback, applicable: true }),
  ];
}

export function toCameraMetrics(details: IntelligenceModels.DeviceHealth.CameraDetails): HealthMetric[] {
  const metrics: HealthMetric[] = [];

  if (details.oldestFootageDate) {
    metrics.push({
      labelKey: 'LOCATION_LIST.OLDEST_FOOTAGE_DATE',
      date: details.oldestFootageDate,
      dateTimezone: details.timezone,
    });
  }
  if (details.imageQuality !== undefined) {
    metrics.push({
      labelKey: 'LOCATION_LIST.IMAGE_QUALITY',
      value: IntelligenceModels.DeviceHealth.ImageQualityState[details.imageQuality],
    });
  }
  if (details.error) {
    metrics.push(errorMetric(details.error));
  }

  return metrics;
}

/**
 * The dialog's "Info" row (`ui-camera-status.component.html:151-158`): the
 * CORE_OFFLINE_CAMERA_UNKNOWN wording for a dead parent Core, otherwise the
 * ISSUES.* copy of the camera's error code. An unmapped code prints the raw
 * code rather than a broken translation key.
 */
function errorMetric(error: IntelligenceModels.DeviceHealth.ErrorInfo): HealthMetric {
  const labelKey = 'LOCATION_LIST.ERROR_INFO';

  if (error.kind === IntelligenceModels.DeviceHealth.ErrorInfoKind.CoreOffline) {
    return { labelKey, valueKey: 'LOCATION_LIST.CORE_OFFLINE_CAMERA_UNKNOWN', wide: true };
  }

  const translationKey = error.code === undefined ? undefined : IssuesModel.errorCodeToTranslationKey[error.code];
  return translationKey
    ? { labelKey, valueKey: `ISSUES.${translationKey}`, wide: true }
    : { labelKey, value: `Error code ${error.code}`, wide: true };
}

/**
 * The dialog's ping-dot mapping, word for word its `pingStateToStatus`
 * (`ui-camera-status.component.ts:164-175`): GOOD → Online, MEDIUM → Unknown,
 * BAD → Offline, anything else (including no state) → Online.
 */
export function pingStatus(state: PulsationModels.PingState | undefined): PulsationModels.ComponentStatus {
  switch (state) {
    case PulsationModels.PingState.MEDIUM:
      return PulsationModels.ComponentStatus.Unknown;
    case PulsationModels.PingState.BAD:
      return PulsationModels.ComponentStatus.Offline;
    case PulsationModels.PingState.GOOD:
    default:
      return PulsationModels.ComponentStatus.Online;
  }
}

/**
 * Core metadata fields. The playback server is the one value that regularly
 * outgrows a grid column, so it spans the row instead of being ellipsised.
 */
export function toCoreMetrics(details: IntelligenceModels.DeviceHealth.CoreDetails): HealthMetric[] {
  const metrics: HealthMetric[] = [];

  if (details.platform) metrics.push({ labelKey: 'LOCATION_LIST.PLATFORM', value: details.platform });
  if (details.swVersion) {
    metrics.push({ labelKey: 'LOCATION_LIST.SW_VERSION', value: details.swVersion });
  }
  if (details.playbackProtocol) {
    metrics.push({ labelKey: 'LOCATION_LIST.PLAYBACK_PROTOCOL', value: details.playbackProtocol });
  }
  if (details.storageAuth !== undefined) {
    metrics.push({
      labelKey: 'LOCATION_LIST.STORAGE_AUTH',
      valueKey: details.storageAuth ? 'COMMON.YES' : 'COMMON.NO',
    });
  }
  if (details.playbackServer) {
    metrics.push({
      labelKey: 'LOCATION_LIST.PLAYBACK_SERVER',
      value: details.playbackServer,
      wide: true,
    });
  }

  return metrics;
}

/**
 * Link speed as a unit, not a bare number: the heartbeat reports Mbit/s, and
 * negative values are its "unknown" sentinel — the same one that used to reach
 * the panel as a literal `-1`.
 */
export function linkSpeedLabel(linkSpeedMbps: number | undefined): string {
  if (linkSpeedMbps === undefined || linkSpeedMbps <= 0) return EM_DASH;
  return `${linkSpeedMbps} Mbps`;
}
